import { useEffect, useMemo, useState } from 'react';

import { useDataProvider } from '@work4all/data';

import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { ErpData } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/ErpData';
import { useErpDataPatcher } from '../../hooks/use-erp-data-patcher';
import {
  ErpConversionGroupByType,
  ErpObject,
  SelectedPosition,
} from '../../types';

interface GroupByArticleQueryProps {
  sourceEntity: Entities;
  data: ErpData[];
  positionPatch: SelectedPosition[];
  groupByType?: ErpConversionGroupByType;
  isEditMode: boolean;
}

export const useGroupByArticleQuery = (
  props: GroupByArticleQueryProps
): {
  erpData: {
    loading: boolean;
    data: ErpObject[];
    refetch: () => void;
  };
} => {
  const { sourceEntity, data: inData, positionPatch, groupByType } = props;
  const requestData = useMemo<DataRequest>(() => {
    return {
      entity: sourceEntity,
      data: {
        id: null,
        contractNumber: null,
        currency: {
          id: null,
          name: null,
        },
        businessPartnerContactCombined: {
          businessPartner: {
            id: null,
            businessPartnerType: null,
            data: {
              customer: {
                id: null,
                name: null,
                number: null,
              },
              supplier: {
                id: null,
                name: null,
                number: null,
              },
            },
          },
        },
        positionList: [
          {
            id: null,
            index: null,
            number: null,
            longtext: null,
            positionKind: null,
            amount: null,
            backlog: null,
            discount: null,
            unit: null,
            insteadOfTotalPrice: null,
            singlePriceNet: null,
            totalPriceNet: null,
            supplierId: null,
            supplier: {
              id: null,
              name: null,
            },
            purchasePrice: null,
            articleNumber: null,
            articleId: null,
            bzObjType: null,
            bzObjMemberId: null,
            posId: null,
            partsListAmount: null,
            objectParentId: null,
            article: {
              id: null,
              number: null,
              name: null,
              unit: {
                id: null,
                name: null,
              },
            },
          },
        ],
      },
      filter: [{ id: { $in: inData.map((x) => x.id) } }],
    };
  }, [inData, sourceEntity]);

  const data = useDataProvider<ErpObject>(requestData);
  const erpData = data.data;

  const [lastErpData, setLastErpData] = useState<ErpObject[]>([]);
  useEffect(() => {
    if (erpData.length) {
      setLastErpData(erpData);
    }
  }, [erpData]);

  // we need to apply singlePriceNet before and we cannot do it if there is any positionPatch
  useErpDataPatcher(
    erpData,
    positionPatch,
    setLastErpData,
    groupByType,
    props.isEditMode
  );

  return {
    erpData: {
      loading: data.loading,
      data: lastErpData,
      refetch: data.refetch,
    },
  };
};
